import { Link } from '@tanstack/react-router';
import { CodeBlock, Callout } from '../../components';

export default function Content() {
  return (
    <>
      <p>
        <strong>Draco</strong> (<code>KHR_draco_mesh_compression</code>) is built to squeeze static mesh geometry as small as
        possible, at the cost of a larger decoder and slower decoding. <strong>Meshopt</strong> (
        <code>EXT_meshopt_compression</code>, from the meshoptimizer library) decodes much faster, ships a decoder under 30 KB,
        also compresses animation, morph targets and instance data, and is designed to be served with gzip or Brotli on top.
        Pick Draco for geometry-heavy static models where download size matters most; pick Meshopt for animated scenes, many
        small models, or when load-time CPU on phones matters.
      </p>
      <p>
        This guide covers how each one works, how they compare on size, speed and features, which engines and tools support
        them, and the exact commands to apply either one.
      </p>

      <h2>What Draco and Meshopt are</h2>

      <h3>Draco (KHR_draco_mesh_compression)</h3>
      <p>
        Draco is Google&apos;s open-source mesh compression library. Its glTF extension, ratified by Khronos, replaces the
        vertex attributes and indices of each mesh primitive with a single Draco-encoded blob. The encoder quantizes the
        attributes, encodes the triangle connectivity (gltf-transform defaults to the edgebreaker method) and entropy-codes
        the result.
      </p>
      <p>
        Two details from the spec matter in practice: the extension only applies to triangle and triangle-strip primitives,
        and Draco may change the order and number of vertices. For Draco&apos;s internals and settings, see our{' '}
        <Link to="/blog/draco-compression">Draco compression guide</Link>.
      </p>

      <h3>Meshopt (EXT_meshopt_compression)</h3>
      <p>
        <a href="https://github.com/zeux/meshoptimizer" target="_blank" rel="noopener noreferrer">meshoptimizer</a> is Arseny
        Kapoulkine&apos;s MIT-licensed library for making meshes smaller and faster to render: vertex cache and overdraw
        optimization, simplification, quantization and a set of compression codecs. Those codecs are exposed to glTF as{' '}
        <code>EXT_meshopt_compression</code>, which Khronos has also ratified.
      </p>
      <p>
        Instead of working per primitive, Meshopt compresses individual <em>buffer views</em>, so it handles any binary data
        in the file: vertex attributes, indices, points and lines, morph target deltas, animation keyframes, and instance
        transforms for <code>EXT_mesh_gpu_instancing</code>. Optional filters (octahedral for normals, quaternion for
        rotations, exponential for floats) trade precision for a smaller result.
      </p>

      <h3>Where KHR_mesh_quantization fits</h3>
      <p>
        Meshopt expects its input to be quantized first. <code>KHR_mesh_quantization</code> lets glTF store positions,
        normals, UVs and tangents as 8- or 16-bit integers instead of 32-bit floats; the spec&apos;s own example shrinks a
        typical PBR vertex from 48 to 20 bytes. Both gltfpack and the gltf-transform <code>meshopt</code> command quantize
        automatically before compressing.
      </p>
      <p>
        A side benefit: quantized attributes stay 8- or 16-bit on the GPU, so they also use less video memory. Draco
        quantizes internally too, but decoders usually expand the data back to floats. three.js&apos;s DRACOLoader, for
        example, outputs <code>Float32Array</code> attributes by default.
      </p>

      <h3>KHR_meshopt_compression, the next version</h3>
      <p>
        Khronos is standardizing Meshopt as <code>KHR_meshopt_compression</code>, which was a release candidate at the time
        of writing. It keeps the EXT bitstream as &quot;version 0&quot; and adds a version 1 format that, according to the
        spec, compresses vertex data better at no extra runtime cost, plus a new color filter. three.js (r183 and later),
        gltfpack and Blender 5.2 already read it, while Babylon.js and gltf-transform currently handle only the EXT version.
        Stick with EXT unless you control the loader.
      </p>

      <h2>Draco vs Meshopt at a glance</h2>
      <table>
        <thead>
          <tr>
            <th></th>
            <th>Draco</th>
            <th>Meshopt</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>glTF extension</td>
            <td>
              <code>KHR_draco_mesh_compression</code>
            </td>
            <td>
              <code>EXT_meshopt_compression</code> (<code>KHR_meshopt_compression</code> in progress)
            </td>
          </tr>
          <tr>
            <td>Works on</td>
            <td>Mesh primitives (triangles only)</td>
            <td>Any buffer view</td>
          </tr>
          <tr>
            <td>Morph targets, animation, instancing data</td>
            <td>Not compressed</td>
            <td>Compressed</td>
          </tr>
          <tr>
            <td>Size before gzip/Brotli</td>
            <td>Usually smaller; designed for maximum ratio</td>
            <td>Larger; designed to be compressed further</td>
          </tr>
          <tr>
            <td>Extra gain from gzip/Brotli</td>
            <td>Little</td>
            <td>Significant</td>
          </tr>
          <tr>
            <td>Decode speed</td>
            <td>Slower (runs in Web Workers in three.js)</td>
            <td>Around 1 GB/s with WebAssembly SIMD, per the spec</td>
          </tr>
          <tr>
            <td>Decoder size (three.js r186)</td>
            <td>About 345 KB, 100 KB gzipped</td>
            <td>About 29 KB, 8 KB gzipped</td>
          </tr>
          <tr>
            <td>Lossy?</td>
            <td>Yes: quantization, may reorder vertices</td>
            <td>Codec is lossless; quantization and filters are lossy</td>
          </tr>
          <tr>
            <td>GPU memory after decoding</td>
            <td>Usually 32-bit floats</td>
            <td>Can stay 8- or 16-bit</td>
          </tr>
        </tbody>
      </table>

      <h2>Compressed size, and why gzip and Brotli change the answer</h2>
      <p>
        Draco&apos;s output is already entropy-coded, so gzip or Brotli on top gains little. The gltf-transform docs say it
        directly: when a model contains &quot;JPEG textures or Draco geometry, gzip is unlikely to add much further
        benefit.&quot;
      </p>
      <p>
        Meshopt does the opposite. The{' '}
        <a
          href="https://github.com/KhronosGroup/glTF/blob/main/extensions/2.0/Vendor/EXT_meshopt_compression/README.md"
          target="_blank"
          rel="noopener noreferrer"
        >
          EXT_meshopt_compression spec
        </a>{' '}
        says the bitstream is built so that &quot;general-purpose compressor can compress it further&quot;, instead of
        being as small as possible on its own. Before transport compression, the Draco file is typically the smaller of the
        two. Meshopt is designed to win back much of that difference once gzip or Brotli is applied.
      </p>
      <p>What that means in practice:</p>
      <ul>
        <li>
          <strong>Compare sizes after compression.</strong> Run <code>gzip -9 -k</code> or <code>brotli</code> on both
          outputs and compare those numbers, because that is what your users download.
        </li>
        <li>
          <strong>Check that your server compresses GLB files.</strong> In DevTools, the response for your .glb should
          have a <code>Content-Encoding: br</code> or <code>gzip</code> header. If it doesn&apos;t, add{' '}
          <code>model/gltf-binary</code> to your server or CDN compression settings. If you can&apos;t, Draco is the safer
          choice.
        </li>
        <li>
          <strong>Mind the decoder on small models.</strong> gltf-transform&apos;s docs note that for geometry under about
          1 MB, the size of Draco&apos;s WASM decoder &quot;may outweigh size savings.&quot;
        </li>
      </ul>

      <h2>Decode speed and decoder size</h2>
      <p>
        Both formats are decoded on the CPU before the data reaches the GPU, so neither makes rendering itself faster. What
        differs is how long decoding takes and how much code the browser downloads first.
      </p>
      <ul>
        <li>
          <strong>Meshopt</strong>: the spec cites around 1 GB/s with WebAssembly SIMD on modern desktop hardware. In three.js
          r186 the decoder is one 29 KB JavaScript file with the WebAssembly embedded. It decodes on the main thread unless
          you call <code>MeshoptDecoder.useWorkers(n)</code>.
        </li>
        <li>
          <strong>Draco</strong>: decoding is slower (gltf-transform&apos;s docs call Meshopt decoding &quot;considerably
          faster&quot;). three.js&apos;s DRACOLoader runs it in up to four Web Workers by default, which keeps the main
          thread free, but the decoder (WASM plus JS wrapper) is about 345 KB and has to arrive before the first model can be
          decoded. The glTF-specific build in <code>libs/draco/gltf/</code> is smaller, about 250 KB.
        </li>
      </ul>
      <p>
        With one model on a fast desktop, you will rarely notice the difference. It shows up on low-end phones, with many or
        large models, and when time to first render matters.
      </p>

      <h2>Streaming and progressive loading</h2>
      <p>
        Neither extension is progressive on its own: each compressed primitive or buffer view is decoded as a whole, and
        three.js&apos;s GLTFLoader downloads the entire GLB before parsing it. Meshopt&apos;s per-buffer-view design does let
        loaders decode straight into GPU-ready buffers, and it
        works with uncompressed fallback buffers (gltfpack&apos;s <code>-cf</code> flag) for loaders that lack the extension.
        The Draco spec also allows an uncompressed fallback. If you need a model to appear coarse first and refine as it
        streams, you need separate LOD files or a plugin such as <code>NEEDLE_progressive</code>, which three.js lists as a
        third-party GLTFLoader plugin.
      </p>

      <h2>Engine and tool support</h2>
      <p>Support as of September 2026, checked against each project&apos;s source or docs:</p>
      <table>
        <thead>
          <tr>
            <th>Engine or tool</th>
            <th>Draco</th>
            <th>Meshopt</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>three.js</td>
            <td>
              Yes, via <code>DRACOLoader</code>
            </td>
            <td>
              Yes, via <code>setMeshoptDecoder</code> (EXT since r122, KHR since r183)
            </td>
          </tr>
          <tr>
            <td>React Three Fiber (drei <code>useGLTF</code>)</td>
            <td>Yes, on by default</td>
            <td>Yes, on by default</td>
          </tr>
          <tr>
            <td>Babylon.js</td>
            <td>Yes, decoder loaded from the Babylon CDN</td>
            <td>EXT only, since 5.0, no setup needed</td>
          </tr>
          <tr>
            <td>
              <code>&lt;model-viewer&gt;</code>
            </td>
            <td>Yes, decoder loaded on demand</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Unity (glTFast)</td>
            <td>Import and export with the Draco for Unity package</td>
            <td>
              Import with <code>com.unity.meshopt.decompress</code>
            </td>
          </tr>
          <tr>
            <td>Godot 4</td>
            <td>Not built in</td>
            <td>Not built in</td>
          </tr>
          <tr>
            <td>Blender</td>
            <td>Import and export</td>
            <td>Import and export since 5.2 LTS (EXT and KHR)</td>
          </tr>
          <tr>
            <td>gltf-transform</td>
            <td>
              <code>draco</code> command
            </td>
            <td>
              <code>meshopt</code> command (EXT); the default for <code>optimize</code>
            </td>
          </tr>
          <tr>
            <td>gltfpack</td>
            <td>Not supported, including as input</td>
            <td>
              <code>-c</code>, <code>-cc</code> (EXT), <code>-cz</code> (KHR)
            </td>
          </tr>
          <tr>
            <td>compress-glb.com</td>
            <td>Yes, with adjustable level and quantization</td>
            <td>No</td>
          </tr>
        </tbody>
      </table>
      <p>
        Godot refuses files that list an unsupported extension as required, so ship uncompressed geometry to Godot unless a
        plugin adds support. Blender 5.1 and earlier refuse files that require Meshopt with &quot;Extension
        EXT_meshopt_compression is not available on this addon version&quot;. To see which extensions a file uses before you pick a loader setup, run it through the free{' '}
        <Link to="/gltf-validator">glTF validator</Link>.
      </p>

      <h2>How to apply each one</h2>
      <h3>gltf-transform</h3>
      <p>
        <a href="https://gltf-transform.dev/cli" target="_blank" rel="noopener noreferrer">
          gltf-transform
        </a>{' '}
        supports both, and can convert a Draco file to Meshopt because it decodes Draco on read:
      </p>
      <CodeBlock language="bash">{`# Draco (defaults: edgebreaker, 14-bit positions, 10-bit normals, 12-bit UVs)
npx @gltf-transform/cli draco input.glb output.glb --quantize-position 14

# Meshopt (EXT_meshopt_compression); --level is "high" (default) or "medium"
npx @gltf-transform/cli meshopt input.glb output.glb --level high

# Full optimization pass; --compress defaults to meshopt
npx @gltf-transform/cli optimize input.glb output.glb --compress draco --texture-compress webp`}</CodeBlock>
      <p>
        Both are lossy, so compress once, as the last step, from your source file. Our{' '}
        <Link to="/blog/gltf-transform-optimize">gltf-transform optimize guide</Link> explains the other flags.
      </p>

      <h3>gltfpack</h3>
      <p>
        <a
          href="https://github.com/zeux/meshoptimizer/tree/master/gltf"
          target="_blank"
          rel="noopener noreferrer"
        >
          gltfpack
        </a>{' '}
        ships with meshoptimizer and compresses with Meshopt only, never Draco. The native binaries from the releases page
        handle larger files and texture compression; the npm package is fine for quick runs.
      </p>
      <CodeBlock language="bash">{`# EXT_meshopt_compression with extra compression
gltfpack -i input.glb -o output.glb -cc

# KHR_meshopt_compression at a higher level (needs a newer loader, e.g. three.js r183+)
gltfpack -i input.glb -o output.glb -cz

# Keep uncompressed fallback buffers for environments without WebAssembly
gltfpack -i input.glb -o output.glb -c -cf`}</CodeBlock>
      <p>
        gltfpack also quantizes, merges meshes and prunes nodes by default. Add <code>-kn</code> to keep named nodes and{' '}
        <code>-km</code> to keep named materials if your code looks them up by name.
      </p>

      <h3>Loading both in three.js</h3>
      <p>One loader can handle either format:</p>
      <CodeBlock language="javascript">{`import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

const loader = new GLTFLoader()
  .setDRACOLoader(new DRACOLoader().setDecoderPath('/draco/'))
  .setMeshoptDecoder(MeshoptDecoder);

const gltf = await loader.loadAsync('/models/scene.glb');`}</CodeBlock>
      <p>
        Decoder paths, KTX2 textures and React Three Fiber are covered in{' '}
        <Link to="/blog/threejs-load-glb">how to load GLB models in three.js</Link>.
      </p>

      <Callout title="Where compress-glb.com fits">
        Our <Link to="/">online GLB compressor</Link> uses Draco for geometry, with adjustable compression level and
        quantization bits, and combines it with texture resizing and WebP or AVIF re-encoding in one pass. It does not
        output Meshopt; if you need Meshopt, use gltf-transform or gltfpack as shown above.
      </Callout>

      <h2>Which one should you use?</h2>
      <ul>
        <li>
          <strong>Static, geometry-heavy models</strong> (CAD, scans, photogrammetry, product models):{' '}
          <strong>Draco</strong>. It usually gives the best compression ratio, and one decoder download is easily paid
          back.
        </li>
        <li>
          <strong>Hosting without gzip or Brotli</strong> (for example files served straight from object storage):{' '}
          <strong>Draco</strong>, since Meshopt counts on transport compression.
        </li>
        <li>
          <strong>Animated characters, morph targets, instanced scenes</strong>: <strong>Meshopt</strong>. Draco leaves
          animation and morph data uncompressed, and that data can be a large part of the file.
        </li>
        <li>
          <strong>Many small models, or mobile-first pages</strong>: <strong>Meshopt</strong>, for its tiny decoder and
          fast decoding.
        </li>
        <li>
          <strong>Files that go back into Blender or Unity</strong>: Draco works in all recent Blender versions, while
          Meshopt needs Blender 5.2 or later. In Unity, glTFast imports both through its optional Draco and meshopt
          packages.
        </li>
        <li>
          <strong>Godot</strong>: neither, unless you add a plugin.
        </li>
      </ul>
      <p>
        Either way, geometry is only half the story. Textures are often the larger part of a GLB, and neither format touches
        them. See <Link to="/blog/reduce-glb-file-size">how to reduce GLB file size</Link> for the full checklist.
      </p>

      <h2>FAQ</h2>

      <h3>Is Meshopt better than Draco?</h3>
      <p>
        Neither is better overall. Draco usually gives smaller geometry before transport compression; Meshopt decodes much
        faster, has a far smaller decoder, compresses animation and morph targets, and closes much of the size gap once the
        file is served with gzip or Brotli.
      </p>

      <h3>What is meshoptimizer?</h3>
      <p>
        meshoptimizer is an open-source C/C++ library by Arseny Kapoulkine that optimizes meshes for GPU rendering and
        compresses vertex and index data. Its codecs power the <code>EXT_meshopt_compression</code> glTF extension, and its
        companion tool gltfpack optimizes whole glTF files.
      </p>

      <h3>Can I convert a Draco GLB to Meshopt?</h3>
      <p>
        Yes. Run <code>npx @gltf-transform/cli meshopt draco.glb meshopt.glb</code>; gltf-transform decodes the Draco data
        and re-encodes it. gltfpack cannot read Draco files. Because both formats are lossy, converting from the original
        uncompressed model gives slightly better quality.
      </p>

      <h3>Does Draco or Meshopt make my model render faster?</h3>
      <p>
        No. Both are decoded before the data reaches the GPU, so they cut download size, not frame time. To render faster,
        reduce draw calls and triangle counts by merging meshes, instancing and simplifying.
      </p>

      <h3>What is the difference between EXT_meshopt_compression and KHR_meshopt_compression?</h3>
      <p>
        KHR is the Khronos-standardized successor. It accepts the EXT bitstream as &quot;version 0&quot; and adds a version
        1 format with better vertex compression and a color filter. Fewer loaders support it so far, so EXT remains the safer default.
      </p>
    </>
  );
}
